import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import { pnrRegex, ticketNumberRegex } from 'utils/validators';
import { ConfirmButton, CancelButton } from 'components/ui/Button';
import AlertBox from 'components/ui/AlertBox';

import {
  PointsInput,
  MandatoryFieldLabel,
  ActionsList,
  Error,
  PointsFormField,
  ClaimMissingPointsWrapper,
  ErrorContainer,
  StyledLabel
} from '../../ClubEurostarMember.styles';
import { StyledRadioLabel, StyledRadio, ErrorCode, ErrorCodeContainer } from '../PointsStatement.styles';

const ClaimMissingPointsForm = ({ onSubmit, onCancel, loading, error, isPackage, isBookingNonExistent, showTcnNotCheckedInMessage }) => {
  const {
    register,
    handleSubmit,
    formState: { isValid, errors }
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      pnr: '',
      tcn: '',
      overrideName: 'no'
    }
  });

  const options = [
    { value: 'no', label: 'interaction.no' },
    { value: 'yes', label: 'interaction.yes' }
  ];

  const [isClicked, setIsClicked] = useState(options[0].value);

  const handleClick = (event) => {
    setIsClicked(event.target.value);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-cy="claim-missing-points-form" data-testid="claim-missing-points-form">
      <ErrorContainer>
        <FormattedMessage id="clubEurostar.claimMissingPoints.rules" />
      </ErrorContainer>
      <ClaimMissingPointsWrapper>
        <PointsFormField>
          <MandatoryFieldLabel htmlFor="pnr">
            <FormattedMessage id="clubEurostar.associatedPNR" />
          </MandatoryFieldLabel>
          <div>
            <PointsInput {...register('pnr', { required: true, pattern: pnrRegex })} type="text" id="pnr" />
          </div>
          {errors.pnr && (
            <Error data-cy="error">
              <FormattedMessage id="clubEurostar.pnrValidationMessage" />
            </Error>
          )}
        </PointsFormField>
        <PointsFormField>
          <MandatoryFieldLabel htmlFor="tcn">
            <FormattedMessage id="clubEurostar.ticketNumber" />
          </MandatoryFieldLabel>
          <div>
            <PointsInput {...register('tcn', { required: true, pattern: ticketNumberRegex })} type="text" id="tcn" />
          </div>
          {errors.tcn && (
            <Error data-cy="error">
              <FormattedMessage id="clubEurostar.tcnValidationMessage" />
            </Error>
          )}
        </PointsFormField>
      </ClaimMissingPointsWrapper>
      {isPackage ? (
        <ErrorContainer>
          <Error data-cy="error">
            <FormattedMessage
              data-testid="claim-missing-points-error-code.isPackageBooking"
              id="clubEurostar.claimMissingPoints.errorMessage.isPackageBooking"
            />
          </Error>
        </ErrorContainer>
      ) : showTcnNotCheckedInMessage ? (
        <ErrorContainer>
          <Error data-cy="error">
            <FormattedMessage
              data-testid="claim-missing-points-error-code.TcnNotCheckedIn"
              id="clubEurostar.claimMissingPoints.errorMessage.TcnNotCheckedIn"
            />
          </Error>
        </ErrorContainer>
      ) : isBookingNonExistent ? (
        <ErrorContainer>
          <Error data-cy="error">
            <FormattedMessage
              data-testid="claim-missing-points-error-code.bookingNotFound"
              id="clubEurostar.claimMissingPoints.errorMessage.bookingNotFound"
              values={{ pnr: isBookingNonExistent }}
            />
          </Error>
        </ErrorContainer>
      ) : null}
      {!isPackage && !isBookingNonExistent && error && error.length ? (
        <>
          <ErrorContainer>
            <Error>
              <FormattedMessage id="clubEurostar.claimMissingPoints.errorMessage" />
            </Error>
          </ErrorContainer>
          {error.map(({ extensions }) => (
            <>
              <ErrorCodeContainer key={extensions.code} data-testid="claim-missing-points-error-code">
                <strong>Error code: </strong>
                <ErrorCode>{extensions?.originalError}</ErrorCode>
              </ErrorCodeContainer>
              {extensions?.originalError === 'NameMismatched' ? (
                <>
                  <StyledLabel>
                    <FormattedMessage id="clubEurostar.claimMissingPoints.validateNameLabel" />
                  </StyledLabel>
                  {options.map((option, index) => (
                    <StyledRadioLabel htmlFor={option.value} key={option.value}>
                      <StyledRadio
                        type="radio"
                        id="overrideName"
                        value={option.value}
                        onClick={handleClick}
                        defaultChecked={index === 0}
                        {...register('overrideName', { required: true })}
                      />
                      <FormattedMessage id={option.label} />
                    </StyledRadioLabel>
                  ))}
                  {isClicked === options[1].value && (
                    <AlertBox variant={AlertBox.variants.WARNING}>
                      <FormattedMessage id="overrideNameValidation.warning" />
                    </AlertBox>
                  )}
                </>
              ) : null}
            </>
          ))}
        </>
      ) : null}

      <ActionsList>
        <CancelButton
          type="button"
          data-cy="claim-missing-points-cancel-button"
          data-testid="claim-missing-points-cancel-button"
          onClick={onCancel}
        >
          <FormattedMessage id="clubEurostar.cancel" />
        </CancelButton>
        <ConfirmButton
          type="submit"
          data-cy="claim-missing-points-confirm-button"
          data-testid="claim-missing-points-confirm-button"
          disabled={!isValid || loading}
          loading={loading}
        >
          <FormattedMessage id="clubEurostar.confirm" />
        </ConfirmButton>
      </ActionsList>
    </form>
  );
};

ClaimMissingPointsForm.propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.array,
  isPackage: PropTypes.bool,
  isBookingNonExistent: PropTypes.bool || PropTypes.string,
  showTcnNotCheckedInMessage: PropTypes.bool
};

export default ClaimMissingPointsForm;
